import * as React from 'react'

import { InfiniteScroll } from '@factorialco/deprecated-design-system'

import { PAGINATION_KIND } from 'modules/core/components/DataTable/constants'
import usePagination from 'modules/core/components/DataTable/hooks/usePagination'
import type { HeadCell, PaginationType, Row } from 'modules/core/components/DataTable/types'

import Skeleton from './Skeleton'

type BodyWithInfiniteScrollProps<T, S extends string> = {
  children: React.ReactNode
  header: HeadCell<S>[]
  loadingMore: boolean
  paginationKind: PaginationType['kind']
  body: Row[]
  selectable?: (item: T) => boolean
}

function BodyWithInfiniteScroll<T, S extends string>({
  children,
  header,
  loadingMore,
  paginationKind,
  body,
  selectable,
}: BodyWithInfiniteScrollProps<T, S>) {
  const { cursorPagination, offsetPagination } = usePagination()
  const { actions } = cursorPagination
  const { currentPage, goToPage, totalPages, totalCount } = offsetPagination

  const loadMore = () => {
    if (loadingMore) return

    if (paginationKind === PAGINATION_KIND.OFFSET) {
      const newPage = currentPage + 1
      if (totalPages && newPage > totalPages) return

      goToPage(currentPage + 1)
    } else if (paginationKind === PAGINATION_KIND.CURSOR) {
      if (actions?.next.disabled) return

      actions?.next.onExecute()
    }
  }

  const getHasMore = () => {
    if (paginationKind === PAGINATION_KIND.OFFSET) {
      const hasMorePages = !!totalPages && currentPage <= totalPages
      const hasMoreItems = !!totalCount && body.length < totalCount

      return hasMorePages && hasMoreItems
    }

    if (paginationKind === PAGINATION_KIND.CURSOR) {
      return !actions?.next.disabled
    }

    return false
  }

  const colSpan = header.length + (selectable ? 1 : 0)

  return (
    <InfiniteScroll
      element="tbody"
      loadMore={loadMore}
      hasMore={getHasMore()}
      colSpan={colSpan}
      loader={
        <Skeleton
          header={header}
          itemsPerPage={5}
          withBody={false}
          selectable={selectable}
          key={0}
        />
      }
    >
      {children}
    </InfiniteScroll>
  )
}

export default BodyWithInfiniteScroll
